import api from "../api/axios";
import { API_ENDPOINTS, STORAGE_KEYS, COOKIE_KEYS } from "../constants";
import { getCookie } from "../utils/cookieUtils";
import {
  TokenDto,
  UserDto,
  PublicRegistrationInitialRequestDto,
  PublicRegistrationValidationRequestDto,
  PublicRegistrationFinishExtendedRequestDto,
  PasswordForgotRequestDto,
  CreateTokenDto,
  UpdateTokenDto,
  DeleteTokenDto,
  TwoFactorLoginVerifyRequestDto,
  TwoFactorLoginBackupRequestDto,
  TwoFactorSendCodeRequestDto,
  TwoFactorVerifyRequestDto,
  TwoFactorBackupCodesResponseDto,
  AuthService,
  LoginStartResponseDto,
} from "../types/api";

class AuthServiceImpl implements AuthService {
  // Registration methods
  async startRegistration(email: string): Promise<void> {
    const data: PublicRegistrationInitialRequestDto = { email };
    await api.post(API_ENDPOINTS.REGISTRATION_START, data);
  }

  async validateRegistrationToken(token: string): Promise<boolean> {
    const data: PublicRegistrationValidationRequestDto = {
      validationToken: token,
    };
    try {
      await api.post(API_ENDPOINTS.REGISTRATION_VERIFY_TOKEN, data);
      return true;
    } catch (error) {
      return false;
    }
  }

  async completeRegistration(
    data: PublicRegistrationFinishExtendedRequestDto,
  ): Promise<TokenDto> {
    const response = await api.put(API_ENDPOINTS.REGISTRATION_START, data);
    return response.data;
  }

  // Login methods
  async login(credentials: CreateTokenDto): Promise<TokenDto> {
    const response = await api.post(API_ENDPOINTS.LOGIN, credentials);
    return response.data;
  }

  async startTwoFactorLogin(
    credentials: CreateTokenDto,
  ): Promise<LoginStartResponseDto> {
    const response = await api.post(API_ENDPOINTS.LOGIN, credentials);
    return response.data;
  }

  async verifyTwoFactorOtp(
    data: TwoFactorLoginVerifyRequestDto,
  ): Promise<TokenDto> {
    const response = await api.post(API_ENDPOINTS.VERIFY_2FA, data);
    return response.data;
  }

  async verifyTwoFactorBackup(
    data: TwoFactorLoginBackupRequestDto,
  ): Promise<TokenDto> {
    const response = await api.post(API_ENDPOINTS.VERIFY_BACKUP, data);
    return response.data;
  }

  // Password Reset methods
  async forgotPassword(email: string): Promise<void> {
    const data: PasswordForgotRequestDto = { email };
    await api.post(API_ENDPOINTS.FORGOT_PASSWORD, data);
  }

  async resetPassword(
    token: string,
    password: string,
    passwordConfirm: string,
  ): Promise<void> {
    const data = { token, password, passwordConfirm };
    await api.post(API_ENDPOINTS.RESET_PASSWORD, data);
  }

  async getCurrentUser(): Promise<UserDto> {
    const tokenRaw = getCookie(COOKIE_KEYS.ACCESS_TOKEN);
    if (!tokenRaw) {
      throw new Error("No token found");
    }

    const response = await api.get(API_ENDPOINTS.GET_CURRENT_USER, {
      headers: {
        Authorization: `Bearer ${JSON.parse(tokenRaw).access_token}`,
      },
    });

    return response.data;
  }

  async getCurrentUserWithToken(accessToken: string): Promise<UserDto> {
    const response = await api.get(API_ENDPOINTS.GET_CURRENT_USER, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  }

  // 2FA Setup methods
  async sendTwoFactorCode(phoneNumber: string): Promise<void> {
    const data: TwoFactorSendCodeRequestDto = { phoneNumber };
    await api.post(API_ENDPOINTS.SEND_2FA_CODE, data);
  }

  async resendTwoFactorSetupCode(): Promise<void> {
    await api.post("/2fa/setup/resend");
  }

  async resendLoginTwoFactorCode(email: string): Promise<void> {
    await api.post(
      `${API_ENDPOINTS.RESEND_LOGIN_2FA}?email=${encodeURIComponent(email)}`,
    );
  }

  async verifyTwoFactorSetup(
    data: TwoFactorVerifyRequestDto,
  ): Promise<TwoFactorBackupCodesResponseDto> {
    const response = await api.post(API_ENDPOINTS.VERIFY_2FA_SETUP, data);
    return response.data;
  }

  // Token Management methods
  async refreshToken(tokenData: UpdateTokenDto): Promise<TokenDto> {
    const response = await api.put("/token", tokenData);
    return response.data;
  }

  async logout(tokenData: DeleteTokenDto): Promise<void> {
    await api.delete(API_ENDPOINTS.LOGOUT, { data: tokenData });
  }

  async logoutAll(): Promise<void> {
    await api.delete(API_ENDPOINTS.LOGOUT_ALL);
  }

  async regenerateBackupCodes(): Promise<TwoFactorBackupCodesResponseDto> {
    const response = await api.post(API_ENDPOINTS.REGENERATE_BACKUP_CODES);
    return response.data;
  }

  async updateCurrentUser(data: {
    displayName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    dentalLicenseNumber?: string;
    specialtyLicenseNumber?: string;
    deaNumber?: string;
    npiNumber?: string;
    specialtyIds?: number[];
    oldPassword?: string;
    password?: string;
  }): Promise<UserDto> {
    const cleanedData = {
      ...data,
      phoneNumber: data.phoneNumber || null,
      dateOfBirth: data.dateOfBirth || null,
      dentalLicenseNumber: data.dentalLicenseNumber || null,
      specialtyLicenseNumber: data.specialtyLicenseNumber || null,
      deaNumber: data.deaNumber || null,
      npiNumber: data.npiNumber || null,
    };
    const response = await api.put("/me", cleanedData);
    return response.data;
  }

  async uploadCurrentUserAvatar(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post("/me/avatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  // Registration phone verification methods
  async sendRegistrationPhoneCode(phoneNumber: string): Promise<void> {
    const registrationToken = localStorage.getItem(STORAGE_KEYS.REGISTRATION_TOKEN);
    if (!registrationToken) {
      throw new Error("No registration token found");
    }

    const data = {
      validationToken: registrationToken,
      phoneNumber: phoneNumber,
    };

    await api.post(API_ENDPOINTS.REGISTRATION_PHONE_SEND, data);
  }

  async verifyRegistrationPhoneCode(code: string): Promise<TokenDto> {
    const registrationToken = localStorage.getItem(
      STORAGE_KEYS.REGISTRATION_TOKEN,
    );
    const email = getCookie(COOKIE_KEYS.EMAIL_HINT);

    let data;

    if (registrationToken) {
      // Registration flow - use validationToken
      data = {
        validationToken: registrationToken,
        code: code,
      };
      const response = await api.post(
        API_ENDPOINTS.REGISTRATION_PHONE_VERIFY,
        data,
      );
      return response.data;
    } else if (email) {
      // Login flow - use email
      data = {
        email: email,
        code: code,
      };
      const response = await api.post(API_ENDPOINTS.VERIFY_2FA, data);
      return response.data;
    } else {
      throw new Error("No registration token or email found");
    }
  }
}

// Export singleton instance
export const authService = new AuthServiceImpl();
export default authService;
